/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import SyncSelect from 'react-select';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import numeral from 'numeral';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { get, isEqual, isEmpty } from 'lodash';
import {
  List,
  CellMeasurerCache,
  AutoSizer,
  CellMeasurer
} from 'react-virtualized';

import DropdownIndicator from '../Filters/Filter/DropdownIndicator';
import Filter from '../Filters/Filter';
import lookerApi from '../../services/looker-api';
import Comment from './Comment';
import CommentThread from './Comment/Thread';
import EmotionsList from './EmotionsList';
import CharacterList from './CharacterList';
import RatingsList from './RatingsList';
import { extractFilterValues } from '../../utils/filters';
import { language as languageOptions } from '../../config/filterOptions';
import ModuleExport from '../ModuleExport';

const Reactions = ({
  query,
  countQuery,
  emotionsQuery,
  characterQuery,
  ratingsQuery,
  filters,
  contentType,
  titleName
}) => {
  const [data, setData] = useState([]);
  const [reactionsCount, setReactionsCount] = useState(null);
  const [emotions, setEmotions] = useState(null);
  const [characters, setCharacters] = useState(null);
  const [ratings, setRatings] = useState(null);
  const [reactionType, setReactionType] = useState([]);
  const [language, setLanguage] = useState([]);
  const [emotionFilters, setEmotionFilters] = useState([]);
  const [characterFilters, setCharacterFilters] = useState([]);
  const [ratingsFilters, setRatingsFilters] = useState([]);
  const [appliedEmotionFilters, setAppliedEmotionFilters] = useState([]);
  const [appliedCharacterFilters, setAppliedCharacterFilters] = useState([]);
  const [appliedRatingsFilters, setAppliedRatingsFilters] = useState([]);
  const [appliedReactionType, setAppliedReactionType] = useState([]);
  const [appliedLanguage, setAppliedLanguage] = useState([]);
  const [sentimentSection, setSentimentSection] = useState('emotions');
  const [loading, setLoading] = useState(false);
  const [sorts, setSorts] = useState(query.sortOptions[0]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedComment, setSelectedComment] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [countLoading, setCountLoading] = useState(false);
  const [emotionsLoading, setEmotionsLoading] = useState(false);
  const [charactersLoading, setCharactersLoading] = useState(false);
  const [ratingsLoading, setRatingsLoading] = useState(false);

  const handleClose = () => {
    setIsModalOpen(false);
    setSelectedComment(null);
  };

  const openModal = comment => () => {
    setSelectedComment(comment);
    setIsModalOpen(true);
  };

  const handleEmotionSelection = emotionName => {
    const e = emotionFilters.filter(eid => eid === emotionName);
    if (e.length) {
      setEmotionFilters(emotionFilters.filter(eid => eid !== emotionName));
    } else {
      setEmotionFilters([...emotionFilters, emotionName]);
    }
  };

  const handleCharacterSelection = characterId => {
    const c = characterFilters.filter(cid => cid === characterId);
    if (c.length) {
      setCharacterFilters(characterFilters.filter(cid => cid !== characterId));
    } else {
      setCharacterFilters([...characterFilters, characterId]);
    }
  };

  const handleRatingSelection = rating => {
    const r = ratingsFilters.filter(r => r === rating);
    if (r.length) {
      setRatingsFilters(ratingsFilters.filter(r => r !== rating));
    } else {
      setRatingsFilters([...ratingsFilters, rating]);
    }
  };

  const formatSentimentHeader = () => {
    const count =
      emotionFilters.length + characterFilters.length + ratingsFilters.length;
    return (
      <>
        <span className="tvt-anticipation-tracker__user-sentiment-title">{`User Sentiment ${
          count ? `(${count})` : ''
        }`}</span>
        <OverlayTrigger
          placement="bottom"
          overlay={
            <Tooltip id="tvt-anticipation-tracker__item-tooltip">
              <div>
                Filter by user sentiment to see reactions by users who voted for
                a specific emotion, character, or rating. The graphs below
                illustrate the amount of reactions that pertain to a specific
                emotion, character, or rating. Select one or more to filter by
                user sentiment.
              </div>
            </Tooltip>
          }
        >
          <FontAwesomeIcon
            className="benchmark-info-icon"
            size="1x"
            icon="info-circle"
          />
        </OverlayTrigger>
      </>
    );
  };

  const countFilterChanges = () =>
    emotionFilters.length +
    characterFilters.length +
    ratingsFilters.length +
    reactionType.length +
    language.length;

  const disableApply = () => {
    const hasNewEmotions = isEqual(emotionFilters, appliedEmotionFilters);
    const hasNewCharacters = isEqual(characterFilters, appliedCharacterFilters);
    const hasNewRatings = isEqual(ratingsFilters, appliedRatingsFilters);
    const hasNewReactionTypes = isEqual(reactionType, appliedReactionType);
    const hasNewLanguage = isEqual(language, appliedLanguage);

    return (
      hasNewEmotions &&
      hasNewCharacters &&
      hasNewRatings &&
      hasNewReactionTypes &&
      hasNewLanguage
    );
  };

  const disableReset = () =>
    isEmpty(emotionFilters) &&
    isEmpty(characterFilters) &&
    isEmpty(ratingsFilters) &&
    isEmpty(reactionType) &&
    isEmpty(language);

  const handleLanguageChange = (_id, option) => {
    setLanguage(option || []);
  };

  const handleReactionTypeChange = (_id, option) => {
    if (!option) {
      return setReactionType([]);
    }

    const hasAll = option.filter(opt => opt.id === 'all').length > 0;
    const selected = get(option, [option.length - 1], '');
    if (option.length > 1 && selected.id === 'all') {
      setReactionType([selected]);
    } else if (hasAll && option.length > 1) {
      setReactionType(option.filter(opt => opt.id !== 'all'));
    } else {
      setReactionType(option);
    }
  };

  const formatFilters = (queryId = '') => ({
    ...filters.fields,
    emotions: {
      id: 'emotions',
      isMulti: true,
      label: 'Emotions',
      value: queryId.includes('Emotion')
        ? [
            { value: '-NULL' },
            ...emotionFilters.map(e => ({ value: e, label: e }))
          ]
        : emotionFilters.map(e => ({ value: e, label: e }))
    },
    characters: {
      id: 'characters',
      isMulti: true,
      label: 'Characters',
      value: queryId.includes('Character')
        ? [
            { value: 'NOT NULL' },
            ...characterFilters.map(c => ({ value: c, label: c }))
          ]
        : characterFilters.map(c => ({ value: c, label: c }))
    },
    ratings: {
      id: 'ratings',
      isMulti: true,
      label: 'Ratings',
      value: queryId.includes('Rating')
        ? [
            { value: 'NOT NULL' },
            ...ratingsFilters.map(r => ({ value: r, label: r }))
          ]
        : ratingsFilters.map(r => ({ value: r, label: r }))
    },
    reactionType: {
      id: 'reactionType',
      isMulti: true,
      label: 'reactionType',
      value: reactionType
    },
    originating_language: {
      id: 'originating_language',
      isMulti: true,
      label: 'originating_language',
      value: language
    }
  });

  const fetchComments = async (sortOption = {}) => {
    setLoading(true);
    const reactionFilters = formatFilters();
    const result = await lookerApi.runInlineQueryStream(
      query.id,
      query.meta,
      {
        ...reactionFilters,
        parentComment: {
          id: 'parentComment',
          isMulti: true,
          label: 'parentComment',
          value: [{ label: '', value: 'NULL' }]
        }
      },
      'json',
      sortOption.value || sorts.value
    );

    if (result && result.data) {
      setData(result.data);
    }
    setLoading(false);
  };

  const fetchLooker = async (q, setStateFunc, setLoadingFunc) => {
    setLoadingFunc(true);
    const reactionFilters = formatFilters(q.id);
    const result = await lookerApi.runInlineQueryStream(
      q.id,
      q.meta,
      reactionFilters,
      'json'
    );

    if (result && result.data) {
      setStateFunc(result.data);
    }
    setLoadingFunc(false);
  };

  const onSubmitFilters = () => {
    setAppliedEmotionFilters(emotionFilters);
    setAppliedCharacterFilters(characterFilters);
    setAppliedRatingsFilters(ratingsFilters);
    setAppliedReactionType(reactionType);
    setAppliedLanguage(language);

    fetchComments();
    fetchLooker(countQuery, setReactionsCount, setCountLoading);
  };

  const onResetFilters = () => {
    setEmotionFilters([]);
    setCharacterFilters([]);
    setRatingsFilters([]);
    setReactionType([]);
    setLanguage([]);
  };

  const fetchContent = () => {
    fetchComments();
    fetchLooker(countQuery, setReactionsCount, setCountLoading);
    fetchLooker(emotionsQuery, setEmotions, setEmotionsLoading);
    fetchLooker(characterQuery, setCharacters, setCharactersLoading);
    fetchLooker(ratingsQuery, setRatings, setRatingsLoading);
  };

  const onSortSelect = option => {
    setSorts(option);
    fetchComments(option);
  };

  const onSelectSentiment = type => () => setSentimentSection(type);

  useEffect(() => {
    const values = extractFilterValues(filters.fields);
    const hasNewValues = !isEqual(values, selectedFilters);

    if (hasNewValues) {
      setSelectedFilters(values);
      fetchContent();
    }
  }, [filters.fields]);

  const renderComment = ({ measurerCache, index, key, parent, style }) => {
    const comment = data[index];

    return (
      <CellMeasurer
        key={key}
        cache={measurerCache}
        columnIndex={0}
        parent={parent}
        rowIndex={index}
      >
        {({ measure }) => (
          <button
            onClick={openModal(comment)}
            key={key}
            style={style}
            className="tvt-reactions__comment-modal-button"
          >
            <Comment data={comment} onImageLoad={measure} />
          </button>
        )}
      </CellMeasurer>
    );
  };

  const measurerCache = new CellMeasurerCache({
    fixedWidth: true,
    defaultHeight: 200
  });

  const formatReactionsCount = () => {
    if (countLoading) {
      return <div className="tvt-reactions__count-loading-bar" />;
    }

    return `${numeral(get(reactionsCount, [0, 'count'], null)).format(
      ','
    )} Reactions`;
  };

  const reactionTypeOptions = [
    { title: 'All', id: 'all', label: 'All', value: null },
    {
      title: 'Text Only',
      id: 'text',
      label: 'Text Only',
      value: 'text only'
    },
    {
      title: 'With Images',
      id: 'images',
      label: 'Images',
      value: 'image'
    }
    // {
    //   title: 'With Memes',
    //   id: 'memes',
    //   label: 'Memes',
    //   value: 'meme'
    // }
  ];

  const getReactionTypeOptions = (e, r, c) => {
    const hasSeasonOrEpisode =
      get(filters, ['fields', 'season', 'value'], null) !== null ||
      get(filters, ['fields', 'episode', 'value'], null) !== null;
    const isDisabled =
      !isEmpty(e) || !isEmpty(r) || !isEmpty(c) || hasSeasonOrEpisode;
    return contentType === 'show'
      ? [
          ...reactionTypeOptions,
          {
            title: 'Show Review',
            isDisabled,
            id: 'show_review',
            label: 'Show Review',
            value: 'show_review'
          }
        ]
      : reactionTypeOptions;
  };

  const disableUserSentiment = rt =>
    contentType === 'show' &&
    rt.filter(t => t.value === 'show_review').length > 0;

  const downloadReportFunc = (queryId, queryFilters, queryMeta) => (
    api,
    type,
    title
  ) =>
    api(
      {
        view: queryId,
        filters: queryFilters,
        queryMeta,
        sorts: sorts.value,
        limit: -1
      },
      type,
      title
    );

  const displayLoadingState = () =>
    [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13].map(num => (
      <div key={num} className="tvt-reactions__comment-loading">
        <div className="tvt-reactions__comment-loading-date" />
        <div className="tvt-reactions__comment-loading-text" />
        <div className="tvt-reactions__comment-loading-nums" />
      </div>
    ));

  const displayComments = () => {
    if (!data.length) {
      return (
        <div className="tvt-reactions__empty-state">
          <div className="tvt-reactions__empty-state-title">
            No Results Found
          </div>
          <div>Try adjusting the filters or keywords.</div>
        </div>
      );
    }

    return (
      <AutoSizer disableHeight>
        {({ width }) => (
          <List
            className="tvt-comment-feed__comments"
            deferredMeasurementCache={measurerCache}
            height={1233}
            width={width}
            onRowsRendered={() => {}}
            rowCount={data.length}
            rowHeight={measurerCache.rowHeight}
            rowRenderer={ops => renderComment({ ...ops, measurerCache })}
          />
        )}
      </AutoSizer>
    );
  };

  return !data ? null : (
    <div className={`tvt-reactions__wrapper tvt-reactions__${contentType}`}>
      <CommentThread
        handleClose={handleClose}
        isOpen={isModalOpen}
        data={selectedComment}
        query={query}
        contentType={contentType}
      />
      <ModuleExport
        fetchData
        showTitle={titleName}
        tableDataDownload={downloadReportFunc(
          query.id,
          formatFilters(),
          query.meta
        )}
        csv
        xls
        png
      />
      <div className="tvt-reactions__comments-list-wrapper">
        <div className="tvt-reactions__header-container">
          <div className="tvt-reactions__header-container-header-text main-reactions-count">
            {formatReactionsCount()}
          </div>
          <div className="tvt-reactions__header-container-dropdown-wrapper">
            Sort By:
            <SyncSelect
              className="tvt-filter"
              classNamePrefix="tvt-filter"
              closeMenuOnSelect
              isSearchable={false}
              components={{
                DropdownIndicator
              }}
              controlShouldRenderValue={false}
              onChange={onSortSelect}
              key="tvt-reactions__sort-select"
              isMulti={false}
              options={query.sortOptions}
              value={sorts}
              placeholder={sorts.label}
            />
          </div>
        </div>
        <div className="tvt-reactions__comments-list">
          {loading ? displayLoadingState() : displayComments()}
        </div>
      </div>

      <div className="tvt-reactions__filter-section-wrapper">
        <div className="tvt-reactions__header-container">
          <div className="tvt-reactions__header-container-header-text">
            Filter Reactions
          </div>
        </div>
        <div className="tvt-reactions__filter-selection-section">
          <div className="tvt-reactions__filter-selection">
            <div className="tvt-reactions__filter-title">Reaction Type:</div>
            <Filter
              className="tvt-reactions__reaction-type-filter-wrapper tvt-filter"
              id="reaction-type"
              key="reaction-type"
              onChange={handleReactionTypeChange}
              showInlineSelections
              isMulti
              options={getReactionTypeOptions(
                emotionFilters,
                ratingsFilters,
                characterFilters
              )}
              label="All"
              hasBoolean={false}
              value={reactionType}
            />
          </div>
          {contentType === 'show' && (
            <div className="tvt-reactions__filter-selection">
              <div className="tvt-reactions__filter-title">Language:</div>
              <Filter
                id="language"
                key="language"
                onChange={handleLanguageChange}
                showInlineSelections
                isMulti
                options={languageOptions}
                label="All"
                hasBoolean={false}
                value={language}
              />
            </div>
          )}
        </div>
        <div className="tvt-reactions__filter-selection-section">
          <div className="tvt-reactions__filter-section-title">
            {formatSentimentHeader()}
          </div>
          {disableUserSentiment(reactionType) ? (
            <div className="tvt-reactions__sentiment-disabled">
              User sentiment filters do not apply to show reviews
            </div>
          ) : (
            <>
              <div className="tvt-reactions__sentiment-nav">
                <button
                  onClick={onSelectSentiment('emotions')}
                  className={`tvt-reactions__sentiment-nav-el ${
                    sentimentSection === 'emotions' ? 'selected' : ''
                  }`}
                >
                  Emotions
                </button>
                <div className="tvt-reactions__sentiment-nav-spacer" />
                <button
                  onClick={onSelectSentiment('characters')}
                  className={`tvt-reactions__sentiment-nav-el ${
                    sentimentSection === 'characters' ? 'selected' : ''
                  }`}
                >
                  Characters
                </button>
                <div className="tvt-reactions__sentiment-nav-spacer" />
                <button
                  onClick={onSelectSentiment('ratings')}
                  className={`tvt-reactions__sentiment-nav-el ${
                    sentimentSection === 'ratings' ? 'selected' : ''
                  }`}
                >
                  Rating
                </button>
              </div>
              <div className="tvt-reactions__sentiment-section">
                {sentimentSection === 'emotions' && (
                  <EmotionsList
                    emotions={emotions}
                    selectedEmotions={emotionFilters}
                    onClick={handleEmotionSelection}
                    loading={emotionsLoading}
                  />
                )}
                {sentimentSection === 'characters' && (
                  <CharacterList
                    characters={characters}
                    selectedIds={characterFilters}
                    onClick={handleCharacterSelection}
                    loading={charactersLoading}
                  />
                )}
                {sentimentSection === 'ratings' && (
                  <RatingsList
                    onClick={handleRatingSelection}
                    selectedRatings={ratingsFilters}
                    ratings={ratings}
                    loading={ratingsLoading}
                  />
                )}
              </div>
            </>
          )}
        </div>

        <div className="tvt-reactions__filter-selection-action-section">
          <button
            disabled={disableApply()}
            onClick={onSubmitFilters}
            className="tvt-reactions__button-apply"
          >
            {`apply ${
              countFilterChanges() > 0 ? `(${countFilterChanges()})` : ''
            }`}
          </button>
          <button
            disabled={disableReset()}
            onClick={onResetFilters}
            className="tvt-reactions__button-cancel"
          >
            reset
          </button>
        </div>
      </div>
    </div>
  );
};

Reactions.propTypes = {
  query: PropTypes.shape({
    id: PropTypes.string,
    meta: PropTypes.shape({}),
    sortOptions: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  countQuery: PropTypes.shape({
    id: PropTypes.string,
    meta: PropTypes.shape({}),
    sortOptions: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  filters: PropTypes.shape({}).isRequired,
  contentType: PropTypes.string
};

Reactions.defaultProps = {
  contentType: 'show'
};

export default Reactions;
